/**
 * @layers/stack — Effect stack state management
 *
 * Manages the ordered list of effect passes (cores) applied to a source image.
 * Each stack item references a core type and its parameters.
 */
export function createStackState(stack) {
    if (!stack || stack.length === 0) {
        return { items: [], selectedId: null };
    }
    const items = stack.map((entry, i) => ({
        id: `stack-${i}`,
        core: entry.core,
        params: entry.params,
        opacity: entry.opacity,
        blend: entry.blend,
        mask: entry.mask ?? null,
        enabled: entry.enabled,
    }));
    return { items, selectedId: items[0]?.id ?? null };
}
export function addStackItem(state, item) {
    const id = `stack-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    return {
        items: [...state.items, { ...item, id }],
        selectedId: id,
    };
}
export function removeStackItem(state, id) {
    const items = state.items.filter((i) => i.id !== id);
    return {
        items,
        selectedId: state.selectedId === id ? items[items.length - 1]?.id ?? null : state.selectedId,
    };
}
export function updateStackItem(state, id, updates) {
    return {
        ...state,
        items: state.items.map((i) => (i.id === id ? { ...i, ...updates } : i)),
    };
}
export function moveStackItem(state, id, direction) {
    const idx = state.items.findIndex((i) => i.id === id);
    if (idx < 0)
        return state;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= state.items.length)
        return state;
    const items = [...state.items];
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    return { ...state, items };
}
export function toggleStackItem(state, id) {
    return {
        ...state,
        items: state.items.map((i) => (i.id === id ? { ...i, enabled: !i.enabled } : i)),
    };
}
export function selectStackItem(state, id) {
    return { ...state, selectedId: id };
}
export function getEnabledItems(state) {
    return state.items.filter((i) => i.enabled);
}
export function estimatePasses(items) {
    const PASS_COSTS = {
        "bevel-emboss": 2,
        "gradient-overlay": 1,
        "pattern-dither": 2,
        "noise-grain": 2,
        "gaussian-blur": 3,
        displacement: 2,
        "color-adjust": 1,
        "color-blend": 1,
        blur: 1,
        lighting: 1,
        outline: 2,
    };
    return items.reduce((sum, item) => sum + (PASS_COSTS[item.core] ?? 1), 0);
}
export function isMobileOk(passes) {
    return passes <= 8;
}
//# sourceMappingURL=index.js.map